import type { EChartsOption } from 'echarts';

import { label, numeric, scalar } from './reportModel';

/** Tableau's ATTR shows * when a mark has more than one value; say so in words. */
export function attribute(value: unknown, plural: string): string {
  const text = label(value);
  return text === '*' ? `Multiple ${plural}` : text;
}

/** Daily send-date product stacks with Tableau ATTR context and count formatting, without currency prefixes. */
export function emailChart(rows: Array<Record<string, unknown>>, dark: boolean, width = 1100): EChartsOption {
  const dates = [...new Set(rows.map((row) => String(scalar(row.send_date))))].sort();
  const products = [...new Set(rows.map((row) => String(row.product_name)))].sort().reverse();
  const index = new Map(rows.map((row) => [`${scalar(row.send_date)}\u0000${row.product_name}`, row]));
  const compact = width < 850;
  const text = dark ? '#eeeeee' : '#555555';
  const palette: Record<string, string> = {
    Cruisebank: '#ff9da7',
    'Limited Time Lux Exclusive': '#f28e2b',
    'Limited Time Lux Partner': '#e15759',
    'Lux Partner Property': '#bab0ac',
    'Lux Premium Collection': '#59a14f',
    Rezdy: '#edc948',
    Tour: '#76b7b2',
    Tourbank: '#9c755f',
    Cruise: '#b07aa1',
    'Bundle and Save': '#edc948',
    __null__: '#4e79a7',
  };
  return {
    animation: false,
    backgroundColor: '#ffffff',
    title: {
      show: !compact,
      text: 'product_name',
      right: 10,
      top: 0,
      textStyle: { fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 'normal', color: text },
    },
    textStyle: { fontFamily: 'Arial, sans-serif', color: text },
    grid: { left: compact ? 52 : 65, right: compact ? 16 : 255, top: 20, bottom: compact ? 96 : 65 },
    legend: {
      type: compact ? 'scroll' : 'plain',
      orient: compact ? 'horizontal' : 'vertical',
      right: compact ? 10 : 8,
      left: compact ? 10 : undefined,
      top: compact ? undefined : 30,
      bottom: compact ? 0 : undefined,
      itemWidth: compact ? 14 : 18,
      itemHeight: compact ? 14 : 18,
      selectedMode: false,
      data: [...products].reverse().map(label),
      // Long product names such as Limited Time Lux Exclusive wrap inside the legend column instead of
      // running past the card edge.
      textStyle: { color: text, fontSize: compact ? 11 : 13, ...(compact ? {} : { width: 210, overflow: 'break' }) },
    },
    tooltip: {
      trigger: 'item',
      confine: true,
      renderMode: 'richText',
      valueFormatter: (value) => (value === null ? '–' : Number(value).toLocaleString('en-AU')),
    },
    xAxis: {
      type: 'category',
      name: 'Send Date',
      nameLocation: 'middle',
      nameGap: 45,
      data: dates,
      axisLine: { lineStyle: { color: '#eeeeee' } },
      axisTick: { show: false },
      axisLabel: {
        color: text,
        hideOverlap: true,
        fontSize: compact ? 11 : 12,
        interval: (_index: number, value: string) => Number(value.slice(8, 10)) % (compact ? 10 : 5) === 0,
        formatter: (value: string) => (compact ? value.slice(5) : value).split('-').reverse().join('/'),
      },
    },
    yAxis: {
      type: 'value',
      name: 'Total Clicks',
      nameLocation: 'middle',
      nameGap: 52,
      splitNumber: 6,
      splitLine: { lineStyle: { color: '#eeeeee' } },
      axisLabel: { color: text, formatter: (value: number) => `${value / 1000}K` },
    },
    series: products.map((product) => ({
      name: label(product),
      type: 'bar',
      stack: 'clicks',
      barCategoryGap: '20%',
      itemStyle: { color: palette[product] ?? '#4e79a7' },
      selectedMode: 'single',
      emphasis: { focus: 'self' },
      select: { itemStyle: { borderColor: '#555555', borderWidth: 2 } },
      data: dates.map((date) => {
        const row = index.get(`${date}\u0000${product}`);
        return {
          value: numeric(row?.total_clicks),
          name: date,
          tooltip: {
            formatter: `${date}\n${label(product)}\nTotal clicks: ${numeric(row?.total_clicks)?.toLocaleString('en-AU') ?? '–'}\nVendor: ${attribute(row?.vendor_attribute, 'vendors')}\nOffer: ${attribute(row?.offer_attribute, 'offers')}`,
          },
        };
      }),
    })),
    aria: {
      enabled: true,
      decal: { show: false },
      description:
        'Total email clicks by send date and product. Hover a mark for its vendor and offer; Multiple means more than one source value. Select a mark to highlight without filtering.',
    },
  };
}
